import { useAuth } from "AuthProvider";
import { RoutePaths } from "enums/RoutePaths";
import { Formik, Form as FormikForm, FormikErrors } from "formik";
import useFetch from "hooks/useFetch";
import useLeaveGuard from "hooks/useLeaveGuard";
import jsQR from "jsqr";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  FormikTextarea,
  FormikTextField,
  Icon,
  IconButton,
  PageHeader,
  Select,
  Skeleton,
  TextField,
  Toggle,
  styles as ui,
  useConfirm,
  useToast,
} from "stories/Admin";
import {
  PaymentSettings,
  paymentSettingsUrl,
} from "pages/PaymentSettingsPage/paymentSettings";
import { CampaignPaymentFields, formatAmounts } from "./campaignPayment";
import {
  CAMPAIGN_KINDS,
  COLLECTION_MODES,
  CampaignFormValues,
  CollectionMode,
  ConnectedProviders,
  MAX_DESCRIPTION_LENGTH,
  campaignPayload,
  deriveCollectionMode,
  formatKr,
  inAppSummary,
  progressLabel,
  progressPercent,
  validateCampaignForm,
} from "./campaignKind";
import CampaignPreview from "./CampaignPreview";
import CategoryEditor from "./CategoryEditor";
import { toCategories } from "./campaignCategories";
import styles from "./CampaignPage.module.scss";
import {
  DISCARD_CHANGES_CONFIRM,
  UnsavedChangesGuard,
  useDiscardGuard,
} from "./unsavedChanges";

interface Campaign extends CampaignPaymentFields {
  id: number;
  name: string;
  description: string;
  /** Mål i kroner. */
  targetAmount: number;
  activated: boolean;
  createdAt: string;
  link: string;
  priority?: number;
  /** Mangler hos eldre server: avledes da fra flaggene. */
  collectionMode?: CollectionMode | null;
  showProgress?: boolean | null;
  /** Read-only: bare satt når fremdrift vises og typen er gaver i appen. */
  raisedOre?: number | null;
  donorCount?: number | null;
  /** Kategorier giveren velger mellom. Mangler hos eldre server. */
  categories?: string[] | null;
}

const PRIORITIES = Array.from({ length: 100 }, (_, index) => index + 1);

/** Options 1–100, plus the current value if it is outside that range. */
const priorityOptions = (current?: number) =>
  current !== undefined && !PRIORITIES.includes(current)
    ? [current, ...PRIORITIES]
    : PRIORITIES;

/** QR code icon (not in the shared icon set). */
const QrIcon: React.FC = () => (
  <svg
    className={ui.icon}
    viewBox="0 0 24 24"
    width={16}
    height={16}
    strokeWidth={2}
    aria-hidden="true"
  >
    <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2M14 18h2v2M18 18h2v2" />
  </svg>
);

const emptyForm: CampaignFormValues = {
  collectionMode: "",
  name: "",
  description: "",
  targetAmount: 0,
  activated: false,
  priority: 0,
  link: "",
  vippsEnabled: false,
  stripeEnabled: false,
  oneTimeEnabled: true,
  monthlyEnabled: false,
  suggestedAmounts: "",
  showProgress: false,
  categories: [],
};

const formFromCampaign = (c: Campaign): CampaignFormValues => ({
  collectionMode: deriveCollectionMode(c),
  name: c.name ?? "",
  description: c.description ?? "",
  targetAmount: c.targetAmount ?? 0,
  activated: !!c.activated,
  priority: c.priority || 0,
  link: c.link || "",
  vippsEnabled: !!c.vippsEnabled,
  stripeEnabled: !!c.stripeEnabled,
  oneTimeEnabled: c.oneTimeEnabled ?? true,
  monthlyEnabled: !!c.monthlyEnabled,
  suggestedAmounts: formatAmounts(c.suggestedAmounts),
  showProgress: !!c.showProgress,
  categories: toCategories(c.categories),
});

type SetField = (field: string, value: any) => void;

/** Tre store valgkort øverst i skjemaet. */
const KindPicker: React.FC<{
  value: CollectionMode | "";
  onChange: (mode: CollectionMode) => void;
  paymentMissing: boolean;
}> = ({ value, onChange, paymentMissing }) => (
  <div
    className={styles.kinds}
    role="radiogroup"
    aria-label="Hva slags kampanje?"
  >
    {COLLECTION_MODES.map((mode) => {
      const kind = CAMPAIGN_KINDS[mode];
      const on = value === mode;
      return (
        <button
          key={mode}
          type="button"
          role="radio"
          aria-checked={on}
          className={`${styles.kind} ${on ? styles.kindOn : ""}`}
          onClick={() => onChange(mode)}
        >
          <span className={styles.kindHead}>
            <span className={`${styles.kindIcon} ${styles[`tone_${mode}`]}`}>
              <Icon name={kind.icon} size={18} />
            </span>
            <b className={styles.kindTitle}>{kind.title}</b>
            <span className={styles.kindCheck} aria-hidden="true">
              {on && <Icon name="check" size={14} strokeWidth={3} />}
            </span>
          </span>
          <span className={styles.kindText}>
            <span>{kind.description}</span>
            {kind.note && (
              <span
                className={`${styles.kindNote} ${
                  kind.note.kind === "warn"
                    ? styles.kindNoteWarn
                    : styles.kindNoteOk
                }`}
              >
                {kind.note.text}
              </span>
            )}
            {mode === "IN_APP" && paymentMissing && (
              <span className={styles.kindWarn}>
                Krever at Vipps eller kort er koblet til under Betaling.
              </span>
            )}
          </span>
        </button>
      );
    })}
  </div>
);

const CampaignPage: React.FC = () => {
  const { selectedOrgNumber, selectedOrgName, mosqueName } = useAuth();
  const previewMosque = selectedOrgName || mosqueName;
  const navigate = useNavigate();
  const { isLoading, error, execute } = useFetch<any>();
  const toast = useToast();
  const confirm = useConfirm();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isCampaignsLoading, setIsCampaignsLoading] = useState(true);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [payment, setPayment] = useState<PaymentSettings | null>(null);
  const paymentApi = useFetch<PaymentSettings>();
  const paymentApiRef = useRef(paymentApi.execute);
  paymentApiRef.current = paymentApi.execute;
  const executeRef = useRef(execute);
  executeRef.current = execute;
  // Lukk, Avbryt, Escape og klikk utenfor spør før ulagrede endringer forkastes.
  const {
    requestClose: requestCloseEdit,
    setDirty: setEditDirty,
    isDirty: isEditDirty,
  } = useDiscardGuard(() => setEditingCampaign(null));
  // Meny, lenker og tilbakeknappen spør også når et av skjemaene er endret.
  const createDirtyRef = useRef(false);
  const setCreateDirty = useCallback((dirty: boolean) => {
    createDirtyRef.current = dirty;
  }, []);
  useLeaveGuard(
    () => createDirtyRef.current || isEditDirty(),
    DISCARD_CHANGES_CONFIRM,
  );

  useEffect(() => {
    if (!selectedOrgNumber) return;
    paymentApiRef
      .current(paymentSettingsUrl(selectedOrgNumber), "GET")
      .then((result) => setPayment(result || null));
  }, [selectedOrgNumber]);

  /** `null` = vet ikke (ikke lastet); da stoppes ingenting her, serveren sjekker. */
  const connected: ConnectedProviders | null = payment
    ? { vipps: !!payment.vipps?.connected, stripe: !!payment.stripe?.connected }
    : null;
  const paymentMissing = !!connected && !connected.vipps && !connected.stripe;

  useEffect(() => {
    if (!selectedOrgNumber) return;
    const fetchCampaigns = async () => {
      try {
        const response = await executeRef.current(
          `/api/v1/campaigns/org/${selectedOrgNumber}`,
          "GET",
        );
        if (response) setCampaigns(response);
      } catch (err: any) {
        console.error("Error fetching campaigns:", err);
      } finally {
        setIsCampaignsLoading(false);
      }
    };
    fetchCampaigns();
  }, [selectedOrgNumber]);

  const validate = (values: CampaignFormValues) =>
    validateCampaignForm(values, connected) as FormikErrors<CampaignFormValues>;

  const handleQRCodeSelect = (setFieldValue: SetField) => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";

    fileInput.onchange = () => {
      const file = fileInput.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      setQrLoading(true);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const context = canvas.getContext("2d");
          context?.drawImage(img, 0, 0, img.width, img.height);
          const imageData = context?.getImageData(0, 0, img.width, img.height);
          const qrCodeResult = imageData
            ? jsQR(imageData.data, img.width, img.height)
            : null;
          if (qrCodeResult?.data) {
            setFieldValue("link", qrCodeResult.data);
          } else {
            toast.error("Fant ikke en gyldig QR-kode.");
          }
          setQrLoading(false);
        };
      };
      reader.readAsDataURL(file);
    };
    fileInput.click();
  };

  const handleSubmit = async (
    values: CampaignFormValues,
    { resetForm }: { resetForm: () => void },
  ) => {
    try {
      const response = await execute("/api/v1/campaigns", "POST", {
        ...campaignPayload(values),
        orgNr: selectedOrgNumber,
      });
      if (response) {
        resetForm();
        setCampaigns([...campaigns, response]);
        toast.success("Kampanjen er lagret.");
      } else {
        toast.error("Kunne ikke lagre kampanjen. Prøv igjen.");
      }
    } catch (error: any) {
      console.error("Error:", error.response || error.message || error);
    }
  };

  const handleDeleteCampaign = async (id: number) => {
    try {
      await execute(`/api/v1/campaigns/${id}`, "DELETE");
      setCampaigns(campaigns.filter((campaign) => campaign.id !== id));
      toast.success("Kampanjen er slettet.");
    } catch (error: any) {
      console.error(
        "Error deleting campaign:",
        error.response || error.message || error,
      );
    }
  };

  const confirmDeleteCampaign = async (campaign: Campaign) => {
    const ok = await confirm({
      title: "Slett kampanje?",
      message: (
        <span>
          Kampanjen <b>«{campaign.name}»</b> fjernes fra appen. Dette kan ikke
          angres.
        </span>
      ),
      confirmLabel: "Slett kampanje",
      tone: "danger",
    });
    if (ok) await handleDeleteCampaign(campaign.id);
  };

  useEffect(() => {
    if (!editingCampaign) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestCloseEdit();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [editingCampaign, requestCloseEdit]);

  const handleSaveEdit = async (values: CampaignFormValues) => {
    if (!editingCampaign) return;
    // Read-only felt fra serveren sendes ikke tilbake.
    const { donationProviders, raisedOre, donorCount, ...current } =
      editingCampaign;
    const body = { ...current, ...campaignPayload(values) };

    try {
      const result = await execute(
        `/api/v1/campaigns/${editingCampaign.id}`,
        "PATCH",
        body,
      );
      if (result === null) {
        toast.error("Kunne ikke lagre endringene. Prøv igjen.");
        return;
      }
      const saved: Campaign =
        result && typeof result === "object"
          ? result
          : { ...body, donationProviders, raisedOre, donorCount };
      setCampaigns(
        campaigns.map((campaign) =>
          campaign.id === editingCampaign.id ? saved : campaign,
        ),
      );
      setEditingCampaign(null);
      toast.success("Endringene er lagret.");
    } catch (error: any) {
      console.error(
        "Error updating campaign:",
        error.response || error.message || error,
      );
    }
  };

  /** Valgene for «Gaver i appen». */
  const renderInAppFields = (
    values: CampaignFormValues,
    errors: FormikErrors<CampaignFormValues>,
    setFieldValue: SetField,
  ) => (
    <fieldset className={styles.section}>
      <legend className={styles.sectionTitle}>Gaver i appen</legend>
      {paymentMissing && (
        <div className={styles.notice} role="status">
          <Icon name="alert" size={18} strokeWidth={2} />
          <div className={styles.noticeBody}>
            <b>Betaling er ikke koblet til ennå</b>
            <span>
              Koble til moskeens egen Vipps eller kort (Stripe) under Betaling,
              så kan folk gi rett i appen. Pengene går rett til moskeen.
            </span>
            <Button
              size="sm"
              icon={<Icon name="key" size={15} />}
              onClick={() => navigate(RoutePaths.PaymentSettings)}
            >
              Gå til Betaling
            </Button>
          </div>
        </div>
      )}
      <div className={styles.choiceGroup}>
        <span className={styles.groupLabel}>Betal med</span>
        <Checkbox
          name="vippsEnabled"
          label="Vipps"
          checked={values.vippsEnabled}
          disabled={!!connected && !connected.vipps && !values.vippsEnabled}
          onChange={(e) => setFieldValue("vippsEnabled", e.target.checked)}
        />
        <Checkbox
          name="stripeEnabled"
          label="Kort (Stripe) – også Apple Pay og Google Pay"
          checked={values.stripeEnabled}
          disabled={!!connected && !connected.stripe && !values.stripeEnabled}
          onChange={(e) => setFieldValue("stripeEnabled", e.target.checked)}
        />
        {connected &&
          !paymentMissing &&
          (!connected.vipps || !connected.stripe) && (
            <span className={styles.help}>
              {!connected.vipps ? "Vipps" : "Kort (Stripe)"} er ikke koblet til.{" "}
              <Link to={RoutePaths.PaymentSettings}>
                Koble til under Betaling
              </Link>
              .
            </span>
          )}
        {errors.vippsEnabled && (
          <span className={styles.error} role="alert">
            {errors.vippsEnabled}
          </span>
        )}
      </div>
      <div className={styles.choiceGroup}>
        <span className={styles.groupLabel}>Type gave</span>
        <Checkbox
          name="oneTimeEnabled"
          label="Engangsgave"
          checked={values.oneTimeEnabled}
          onChange={(e) => setFieldValue("oneTimeEnabled", e.target.checked)}
        />
        <Checkbox
          name="monthlyEnabled"
          label="Fast månedlig gave"
          checked={values.monthlyEnabled}
          onChange={(e) => setFieldValue("monthlyEnabled", e.target.checked)}
        />
        {errors.oneTimeEnabled && (
          <span className={styles.error} role="alert">
            {errors.oneTimeEnabled}
          </span>
        )}
      </div>
      <TextField
        label="Forslag til beløp (kr)"
        inputMode="numeric"
        placeholder="100, 200, 500"
        value={values.suggestedAmounts}
        onChange={(e) => setFieldValue("suggestedAmounts", e.target.value)}
        error={errors.suggestedAmounts}
        help="Skill med komma. Giveren kan også skrive inn eget beløp."
      />
      <CategoryEditor
        value={values.categories}
        onChange={(next) => setFieldValue("categories", next)}
        error={
          typeof errors.categories === "string" ? errors.categories : undefined
        }
      />
    </fieldset>
  );

  /** «Lenke til innsamling»: lenken knappen åpner. */
  const renderLinkFields = (setFieldValue: SetField) => (
    <fieldset className={styles.section}>
      <legend className={styles.sectionTitle}>Lenke til innsamling</legend>
      <FormikTextField
        name="link"
        label="Lenke"
        required
        inputMode="url"
        placeholder="https://qr.vipps.no/…"
        help="F.eks. moskeens Vipps-side eller egen innsamlingsside. iBønn ser ikke hvor mye som blir gitt via lenken."
      />
      <div className={styles.qrRow}>
        <Button
          variant="secondary"
          size="sm"
          loading={qrLoading}
          onClick={() => handleQRCodeSelect(setFieldValue)}
          icon={<QrIcon />}
        >
          Les QR-kode fra bilde
        </Button>
        <span className={styles.help}>
          Har du en QR-kode, fylles lenken inn fra et bilde av den.
        </span>
      </div>
    </fieldset>
  );

  /** Felt for skjemaet, både «Ny kampanje» og «Rediger kampanje». */
  const renderFields = (
    values: CampaignFormValues,
    errors: FormikErrors<CampaignFormValues>,
    setFieldValue: SetField,
  ) => {
    const mode = values.collectionMode;
    return (
      <>
        <div className={styles.kindField}>
          <span className={styles.kindLabel}>Hva slags kampanje?</span>
          <KindPicker
            value={mode}
            paymentMissing={paymentMissing}
            onChange={(m) => setFieldValue("collectionMode", m)}
          />
        </div>
        {mode && (
          <>
            <FormikTextField
              name="name"
              label="Navn"
              required
              placeholder={
                mode === "INFO"
                  ? "F.eks. Ny moské"
                  : "F.eks. Ramadan-innsamling"
              }
            />
            <FormikTextarea
              name="description"
              label="Beskrivelse"
              required
              rows={3}
              placeholder="Fortell hva pengene skal brukes til …"
              maxLength={MAX_DESCRIPTION_LENGTH}
              maxChars={MAX_DESCRIPTION_LENGTH}
            />
            {mode === "IN_APP" &&
              renderInAppFields(values, errors, setFieldValue)}
            {mode === "LINK" && renderLinkFields(setFieldValue)}
            <FormikTextField
              name="targetAmount"
              label="Mål (kr)"
              type="number"
              min={0}
              placeholder="F.eks. 150000"
              help="Valgfritt. La stå 0 hvis kampanjen ikke har et mål."
            />
            {mode === "IN_APP" && (
              <div className={styles.toggleField}>
                <Toggle
                  label={
                    <span className={styles.toggleLabel}>
                      Vis fremdrift i appen
                    </span>
                  }
                  name="showProgress"
                  checked={values.showProgress}
                  onChange={(e) =>
                    setFieldValue("showProgress", e.target.checked)
                  }
                />
                <span className={styles.help}>
                  Viser f.eks. «38 000 av 150 000 kr · 24 givere» på kampanjen.
                </span>
              </div>
            )}
            <Select
              label="Prioritet"
              value={values.priority ?? 0}
              onChange={(e) =>
                setFieldValue("priority", parseInt(e.target.value || "50", 10))
              }
              help="Jo lavere tall, jo høyere opp i appen. 1–100."
            >
              {priorityOptions(values.priority).map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </Select>
            <Toggle
              label={
                <span className={styles.toggleLabel}>
                  Vis kampanjen i appen
                </span>
              }
              name="activated"
              checked={values.activated}
              onChange={(e) => setFieldValue("activated", e.target.checked)}
            />
          </>
        )}
      </>
    );
  };

  const renderCampaign = (campaign: Campaign) => {
    const mode = deriveCollectionMode(campaign);
    const kind = CAMPAIGN_KINDS[mode];
    const hasGoal = !!campaign.targetAmount && campaign.targetAmount > 0;
    const raised = campaign.raisedOre;
    // Gaver i appen uten tilkoblet betaling: knappen vises ikke i appen.
    const noProvider =
      mode === "IN_APP" &&
      Array.isArray(campaign.donationProviders) &&
      campaign.donationProviders.length === 0;
    return (
      <article key={campaign.id} className={styles.item}>
        <div className={styles.itemTitleRow}>
          <span
            className={`${styles.itemIcon} ${styles[`tone_${mode}`]}`}
            aria-hidden="true"
          >
            <Icon name={kind.icon} size={18} />
          </span>
          <div className={styles.itemHead}>
            <b className={styles.itemTitle}>{campaign.name}</b>
            <span className={styles.itemBadges}>
              <Badge tone={kind.tone}>{kind.badge}</Badge>
              {campaign.activated ? (
                <Badge tone="mint">Aktiv</Badge>
              ) : (
                <Badge tone="grey">Inaktiv</Badge>
              )}
            </span>
          </div>
          <IconButton
            label="Rediger"
            icon={<Icon name="edit" size={17} strokeWidth={1.9} />}
            onClick={() => setEditingCampaign(campaign)}
          />
          <IconButton
            label="Slett"
            icon={<Icon name="trash" size={17} strokeWidth={1.9} />}
            onClick={() => confirmDeleteCampaign(campaign)}
          />
        </div>
        {campaign.description && (
          <span className={styles.itemText}>{campaign.description}</span>
        )}
        {mode === "IN_APP" && raised != null && (
          <div className={styles.progressBlock}>
            {hasGoal && (
              <div
                className={styles.progress}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progressPercent(raised, campaign.targetAmount)}
                aria-label="Fremdrift mot målet"
              >
                <span
                  style={{
                    width: `${progressPercent(raised, campaign.targetAmount)}%`,
                  }}
                />
              </div>
            )}
            <span className={styles.progressText}>
              {progressLabel(
                raised,
                campaign.targetAmount,
                campaign.donorCount,
              )}
            </span>
          </div>
        )}
        <dl className={styles.dl}>
          <dt>Mål</dt>
          <dd className={ui.num}>
            {hasGoal ? formatKr(campaign.targetAmount) : "Ingen mål"}
          </dd>
          {mode === "IN_APP" && (
            <>
              <dt>Gaver med</dt>
              <dd>{inAppSummary(campaign)}</dd>
              {toCategories(campaign.categories).length > 0 && (
                <>
                  <dt>Kategorier</dt>
                  <dd title={toCategories(campaign.categories).join(", ")}>
                    {toCategories(campaign.categories).join(", ")}
                  </dd>
                </>
              )}
              <dt>Fremdrift</dt>
              <dd>
                {campaign.showProgress ? "Vises i appen" : "Vises ikke i appen"}
              </dd>
            </>
          )}
          {mode === "LINK" && campaign.link && (
            <>
              <dt>Lenke</dt>
              <dd className={styles.link} title={campaign.link}>
                <a href={campaign.link} target="_blank" rel="noreferrer">
                  {campaign.link}
                </a>
              </dd>
            </>
          )}
          <dt>Prioritet</dt>
          <dd>{campaign.priority ? campaign.priority : "Ikke satt"}</dd>
        </dl>
        {noProvider && (
          <Alert tone="warn">
            Ingen betaling er koblet til, så gaveknappen vises ikke i appen.{" "}
            <Link to={RoutePaths.PaymentSettings}>Gå til Betaling</Link>.
          </Alert>
        )}
      </article>
    );
  };

  return (
    <>
      <PageHeader
        title="Kampanjer"
        subtitle="Innsamlinger og prosjekter som vises i appen. Folk kan gi rett i appen, via en lenke, eller kampanjen kan bare være informasjon."
        actions={
          <Button
            variant="secondary"
            icon={<Icon name="chart" size={16} />}
            onClick={() => navigate(RoutePaths.Donations)}
          >
            Se innsamlet
          </Button>
        }
      />

      <div className={styles.layout}>
        <Card title="Ny kampanje">
          <Formik
            initialValues={emptyForm}
            validate={validate}
            onSubmit={handleSubmit}
          >
            {({ isValid, dirty, setFieldValue, values, errors }) => (
              <FormikForm className={styles.withPreview} noValidate>
                <UnsavedChangesGuard onDirtyChange={setCreateDirty} />
                <div className={styles.form}>
                  {renderFields(values, errors, setFieldValue)}
                  {values.collectionMode ? (
                    <Button
                      type="submit"
                      loading={isLoading}
                      disabled={!(isValid && dirty)}
                      icon={<Icon name="check" size={16} strokeWidth={2} />}
                    >
                      {isLoading ? "Lagrer …" : "Lagre kampanje"}
                    </Button>
                  ) : (
                    <span className={styles.help}>
                      Velg en av de tre for å fortsette.
                    </span>
                  )}
                  {error && (
                    <Alert tone="error">
                      {error.message || "Noe gikk galt. Prøv igjen senere."}
                    </Alert>
                  )}
                </div>
                <div className={styles.previewCol}>
                  <CampaignPreview values={values} mosqueName={previewMosque} />
                </div>
              </FormikForm>
            )}
          </Formik>
        </Card>

        <Card title="Alle kampanjer">
          {isCampaignsLoading ? (
            <div className={styles.list}>
              {[0, 1].map((i) => (
                <Skeleton key={i} height={150} radius={20} />
              ))}
            </div>
          ) : campaigns.length > 0 ? (
            <div className={styles.list}>{campaigns.map(renderCampaign)}</div>
          ) : (
            <EmptyState icon="heart" title="Ingen kampanjer">
              Kampanjer du lagrer vises her.
            </EmptyState>
          )}
        </Card>
      </div>

      {editingCampaign && (
        <div
          className={ui.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) requestCloseEdit();
          }}
        >
          <div
            className={`${ui.modal} ${styles.modal}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-campaign-title"
          >
            <Formik
              initialValues={formFromCampaign(editingCampaign)}
              validate={validate}
              onSubmit={handleSaveEdit}
            >
              {({
                isValid,
                dirty,
                setFieldValue,
                values,
                errors,
                isSubmitting,
              }) => (
                <FormikForm noValidate>
                  <UnsavedChangesGuard onDirtyChange={setEditDirty} />
                  <h2 className={ui.mh} id="edit-campaign-title">
                    <span className={ui.mhIcon} aria-hidden="true">
                      <Icon name="edit" size={18} />
                    </span>
                    Rediger kampanje
                    <IconButton
                      className={styles.modalClose}
                      label="Lukk"
                      icon={<Icon name="x" size={18} strokeWidth={2} />}
                      onClick={requestCloseEdit}
                    />
                  </h2>
                  <div className={`${ui.mb} ${styles.withPreview}`}>
                    <div className={styles.form}>
                      {renderFields(values, errors, setFieldValue)}
                    </div>
                    <div className={styles.previewCol}>
                      <CampaignPreview
                        values={values}
                        real={editingCampaign}
                        mosqueName={previewMosque}
                      />
                    </div>
                  </div>
                  <div className={ui.mf}>
                    <Button variant="secondary" onClick={requestCloseEdit}>
                      Avbryt
                    </Button>
                    <Button
                      type="submit"
                      disabled={!(isValid && dirty)}
                      loading={isSubmitting}
                    >
                      Lagre endringer
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </div>
        </div>
      )}
    </>
  );
};

export default CampaignPage;
